import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import ReactPaginate from 'react-paginate';
import './Home.css';
import { Helmet, HelmetProvider } from 'react-helmet-async';

function Home() {
  const [products, setProducts] = useState([]);
  const [priceFilter, setPriceFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState('detailed');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const filterMenuRef = useRef(null);
  const catalogRef = useRef(null);
  const hasAutoScrolledRef = useRef(false);
  const location = useLocation();

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const searchParams = new URLSearchParams(location.search);
    const searchQuery = searchParams.get('search') || '';
    const query = new URLSearchParams({
      ...(searchQuery && { search: searchQuery }),
      price: priceFilter,
      type: typeFilter,
      limit: isMobile ? 10 : 12,
      offset: currentPage * (isMobile ? 10 : 12),
    }).toString();
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/products${query ? `?${query}` : ''}`);
      setProducts(response.data.products || response.data);
      setTotalPages(Math.ceil((response.data.total || response.data.length) / (isMobile ? 10 : 12)));
      setLoading(false);
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]);
      setTotalPages(0);
      setLoading(false);
    }
  }, [location.search, priceFilter, typeFilter, currentPage, isMobile]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
        setShowFilterMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    hasAutoScrolledRef.current = false;
  }, [location.key]);

  useEffect(() => {
    if (location.pathname !== '/products' || loading || !catalogRef.current || hasAutoScrolledRef.current) {
      return undefined;
    }
    const timer = setTimeout(() => {
      const top = catalogRef.current.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
      hasAutoScrolledRef.current = true;
    }, 30);
    return () => clearTimeout(timer);
  }, [location.pathname, loading]);

  const handlePageClick = (data) => {
    setCurrentPage(data.selected);
    window.scrollTo(0, 0);
  };

  const truncateDescription = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text;
  };

  const typeLabelMap = {
    expert_advisor: 'Expert Advisor',
    indicator: 'Indicator',
    source_code: 'Source Code',
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <div className="home">
        <Helmet>
          <title>Vix Algo Trading Store - Precision Trading Algorithms</title>
          <meta name="keywords" content="MQL5, Expert Advisors, Indicators, Source Codes, Trading Algorithms" />
          <meta name="author" content="Vix Algo Trading" />
          <meta name="description" content="Curated MQL5 Expert Advisors, Indicators, and Source Codes built for disciplined, high-signal trading." />
        </Helmet>

        <header className="hero">
          <div className="hero-content">
            <p className="hero-eyebrow">MT5 trading tools</p>
            <h1>Trade with precision. Ship with confidence.</h1>
            <p className="hero-lead">
              Curated MQL5 strategies, indicators, and source code built for disciplined execution.
              Choose a ready-to-run Expert Advisor or customize your own edge.
            </p>
            <div className="hero-actions">
              <a href="#catalog" className="primary-cta">Browse Catalog</a>
              <Link to="/contact-us" className="ghost-cta">Talk to the team</Link>
            </div>
            <div className="hero-highlights">
              <div className="highlight-card">
                <p className="highlight-title">Signal Quality</p>
                <p className="highlight-text">Built-in risk checks, market filters, and smart exits.</p>
              </div>
              <div className="highlight-card">
                <p className="highlight-title">Instant Delivery</p>
                <p className="highlight-text">Download immediately and deploy on MT5 in minutes.</p>
              </div>
              <div className="highlight-card">
                <p className="highlight-title">Transparent Pricing</p>
                <p className="highlight-text">Free and premium tools with clear licensing.</p>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="visual-panel">
              <div className="visual-header">
                <span className="visual-pill">Live Strategy Stack</span>
                <span className="visual-pill subtle">MQL5 Ready</span>
              </div>
              <div className="visual-body">
                <p className="visual-title">Structured tools for disciplined execution.</p>
                <p className="visual-copy">
                  Explore Expert Advisors, indicators, and source code built for practical MT5 deployment.
                </p>
              </div>
              <div className="visual-footer">
                <span className="visual-metric">Risk Managed</span>
                <span className="visual-metric">Modular Signals</span>
                <span className="visual-metric">Audit Friendly</span>
              </div>
            </div>
          </div>
        </header>

        <section className="insights">
          <div className="insight-card">
            <p className="insight-label">Strategy</p>
            <h3>Purpose-built logic</h3>
            <p>Each product includes market-aware rules to keep entries disciplined.</p>
          </div>
          <div className="insight-card">
            <p className="insight-label">Delivery</p>
            <h3>Zero-friction setup</h3>
            <p>Download, install, and validate within a single trading session.</p>
          </div>
          <div className="insight-card">
            <p className="insight-label">Support</p>
            <h3>Built for iteration</h3>
            <p>Source code options make it easy to iterate or personalize.</p>
          </div>
        </section>

        <section className="catalog" id="catalog" ref={catalogRef}>
          <div className="filter-panel" ref={filterMenuRef}>
            <div className="filter-header">
              <div>
                <h2>Browse the catalog</h2>
                <p className="result-count">
                  {Array.isArray(products) ? `${products.length} results shown` : 'Results loading'}
                </p>
              </div>
              <div className="view-toggle">
                <button
                  type="button"
                  className={`toggle-button ${viewMode === 'detailed' ? 'active' : ''}`}
                  onClick={() => setViewMode('detailed')}
                >
                  Detailed
                </button>
                <button
                  type="button"
                  className={`toggle-button ${viewMode === 'compact' ? 'active' : ''}`}
                  onClick={() => setViewMode('compact')}
                >
                  Compact
                </button>
              </div>
              {isMobile && (
                <button
                  type="button"
                  className={`filter-toggle ${showFilterMenu ? 'open' : ''}`}
                  onClick={() => setShowFilterMenu(!showFilterMenu)}
                  aria-label="Toggle filters"
                >
                  Filters
                </button>
              )}
            </div>
            <div className={`filter-controls ${isMobile ? 'mobile' : ''} ${showFilterMenu ? 'open' : ''}`}>
              <div className="filter-group">
                <label htmlFor="price-filter">Price</label>
                <select
                  id="price-filter"
                  value={priceFilter}
                  onChange={(e) => {
                    setPriceFilter(e.target.value);
                    setShowFilterMenu(false);
                  }}
                  className="filter-dropdown"
                >
                  <option value="all">All</option>
                  <option value="free">Free</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div className="filter-group">
                <label htmlFor="type-filter">Type</label>
                <select
                  id="type-filter"
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setShowFilterMenu(false);
                  }}
                  className="filter-dropdown"
                >
                  <option value="all">All</option>
                  <option value="expert_advisor">Expert Advisors</option>
                  <option value="indicator">Indicators</option>
                  <option value="source_code">Source Codes</option>
                </select>
              </div>
            </div>
          </div>

          <div className={`product-list ${viewMode}`}>
            {Array.isArray(products) && products.length > 0 ? (
              products.map((product) => {
                const isFreeProduct = product.price === 0 || product.price === '0';
                return (
                  <div key={product.id} className={`product-card ${viewMode}`}>
                    <div className={`product-thumbnail-shell ${viewMode}`}>
                      {product.imagePath && (
                        <img
                          src={`${process.env.REACT_APP_API_URL}/${product.imagePath}`}
                          alt={product.name}
                          className="product-thumbnail"
                          width={viewMode === 'compact' ? (isMobile ? 100 : 200) : (isMobile ? 150 : 200)}
                          height={viewMode === 'compact' ? (isMobile ? 100 : 200) : (isMobile ? 150 : 200)}
                        />
                      )}
                    </div>
                    <div className="product-info">
                      <div className="product-meta">
                        <span className="product-type">
                          {typeLabelMap[product.type] || 'Product'}
                        </span>
                        <span className={`price-tag ${isFreeProduct ? 'free' : ''}`}>
                          {isFreeProduct ? 'Free' : `$${product.price}`}
                        </span>
                      </div>
                      <h2 className="product-name">{product.name}</h2>
                      {viewMode !== 'compact' && (
                        <div className="product-description">
                          <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
                            {truncateDescription(product.description, 260)}
                          </ReactMarkdown>
                        </div>
                      )}
                      <Link to={`/product/${product.id}`} className="view-details">View Details</Link>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-state">
                <h3>No products available right now.</h3>
                <p>We are refreshing the catalog. Check back soon for new launches.</p>
              </div>
            )}
          </div>
        </section>

        {totalPages > 1 && (
          <ReactPaginate
            previousLabel={'Previous'}
            nextLabel={'Next'}
            breakLabel={'...'}
            pageCount={totalPages}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            onPageChange={handlePageClick}
            containerClassName={'pagination'}
            activeClassName={'active'}
            pageClassName={'page-item'}
            pageLinkClassName={'page-link'}
            previousClassName={'page-item'}
            nextClassName={'page-item'}
            previousLinkClassName={'page-link'}
            nextLinkClassName={'page-link'}
            breakClassName={'page-item'}
            breakLinkClassName={'page-link'}
          />
        )}
      </div>
    </HelmetProvider>
  );
}

export default Home;
